import { error } from '@js/logger';

/**
 * This is a utility class to make final checks and assurances after the checkout button is pressed.
 *
 * Some use cases:
 *  - Making sure a user has accepted terms
 *  - Adding extra services to the cart / items
 *  - Setting cart attributes
 *
 * @example
 * CheckoutHandler.registerPromise('setHeroAttribute', () => {
 *   return new Promise(async (resolve, reject) => {
 *     try {
 *       await window.SolarisCart.setAttribute('hero', true);
 *       resolve();
 *     } catch (e) {
 *       reject();
 *     }
 *   })
 * });
 */

export const checkoutBtnEl = () => {
  return document.querySelector('#Checkout');
};

export const disableCheckoutBtn = (disable) => {
  const btn = checkoutBtnEl();
  if (!btn) return;

  const loading = btn.querySelector('span');

  if (disable) {
    btn.setAttribute('disabled', '');
    loading.innerText = '';
    loading.classList.add('loading-ellipsis');
  } else {
    btn.removeAttribute('disabled');
    loading.innerText = 'Check Out';
    loading.classList.remove('loading-ellipsis');
  }
};

export default class CheckoutHandler {
  constructor() {
    this.promises = {};
    this.status = null;

    this.attachEventToCheckoutBtn();
  }

  registerPromise(key, fun) {
    this.promises[key] = fun;
  }

  unregisterPromise(key) {
    delete this.promises[key];
  }

  attachEventToCheckoutBtn() {
    const checkoutBtn = checkoutBtnEl();
    if (!checkoutBtn) return;

    checkoutBtn.addEventListener('click', (e) => {
      if (this.status === 'resolving') return e.preventDefault();
      if (this.status === 'resolved' || !Object.keys(this.promises).length) return true;

      e.preventDefault();

      this.status = 'resolving';
      document.querySelector('body').style.cursor = 'progress';
      disableCheckoutBtn(true);

      const promises = Object.values(this.promises).reduce((acc, promise) => {
        acc.push(promise());
        return acc;
      }, []);

      return Promise.all(promises)
        .then(() => {
          this.status = 'resolved';
          disableCheckoutBtn(false);
          checkoutBtnEl().click();

          // algolia conversion event
          // TODO: We should technically get all of the object ids from the cart and send them all at once
          const params = new URL(window.location.href).searchParams;
          const oid = params.get('oid');

          if (oid) {
            window.algoliainsights.pushEvents({
              events: [
                {
                  index: window.ALGOLIA_INDEX,
                  eventType: 'click',
                  eventName: 'Initiated Checkout',
                  userToken: window.userToken,
                  objectIDs: [oid],
                },
              ],
            });
          }
        })
        .catch((e) => {
          this.status = null;
          disableCheckoutBtn(false);

          error(e);
        })
        .finally(() => {
          document.querySelector('body').style.cursor = null;
        });
    });
  }
}
